import {
  OfflineAccessSetupPayload,
  OfflineAccessSetupResponse,
  GetOfflineAccessSetupParams,
  OfflineAccessRequestPayload,
  OfflineAccessRequestResponse,
  OfflineAccessVerifyPayload,
  OfflineAccessVerifyResponse,
  OfflineAccessQuestionsParams,
  OfflineAccessQuestionPayload,
  OfflineAccessQuestionAnswerPayload,
  OfflineAccessQuestionVoteParams,
  OfflineAccessDirectMessagePayload,
  OfflineAccessDirectMessageParams,
  OfflineAccessDirectMessageItem,
  OfflineAccessLink,
  CreateOfflineAccessLinkPayload,
  GetOneOfflineAccessLinkParams,
  ListOfflineAccessLinksParams,
  SetLinkAccessArgs,
  DeleteOfflineAccessLinkArgs,
} from "interfaces";
import { DiscussionInterface } from "interfaces/interfaces-data";
import instance from "lib/axiosInterceptor";

// The shared-playlist link endpoints answer 401 when the signed-in user has no
// rights on that playlist (co-presenters, for instance) — not because the
// session expired. Opt those calls out of the interceptor's logout redirect so
// the playlist page can fall back to a read-only state instead.
const RESOURCE_SCOPED_CONFIG = {
  skipAuthRedirect: true,
  suppressToast: true,
} as const;

// 1. Configure offline access from the organizer setup UI.
export async function setupOfflineAccessService(
  payload: OfflineAccessSetupPayload,
): Promise<OfflineAccessSetupResponse> {
  const response = await instance.post<OfflineAccessSetupResponse>(
    "/OfflineAccess/Setup",
    payload,
  );
  return response.data;
}

// 2. Load the existing offline access config / public access form.
export async function getOfflineAccessSetupService(
  params: GetOfflineAccessSetupParams,
): Promise<OfflineAccessSetupResponse> {
  const { resourceType, resourceId, eventId } = params;
  const response = await instance.get<OfflineAccessSetupResponse>(
    `/OfflineAccess/Setup/${resourceType}/${resourceId}`,
    {
      params: eventId ? { eventId } : undefined,
    },
  );
  return response.data;
}

// 2a. [Multi-link] Create a new shared-playlist link.
export async function createOfflineAccessLinkService(
  payload: CreateOfflineAccessLinkPayload,
): Promise<OfflineAccessLink> {
  const response = await instance.post<OfflineAccessLink>(
    "/OfflineAccess/Links",
    payload,
    RESOURCE_SCOPED_CONFIG,
  );
  return response.data;
}

// 2b. [Multi-link] List all shared-playlist links for a resource.
export async function listOfflineAccessLinksService(
  params: ListOfflineAccessLinksParams,
): Promise<OfflineAccessLink[]> {
  const { resourceType, resourceId, eventId, teamId } = params;
  const response = await instance.get<OfflineAccessLink[]>(
    `/OfflineAccess/Links/${resourceType}/${resourceId}`,
    {
      ...RESOURCE_SCOPED_CONFIG,
      params: {
        ...(eventId ? { eventId } : {}),
        ...(teamId ? { teamId } : {}),
      },
    },
  );
  return response.data;
}

// 2c. [Multi-link] Load one link's setup for the edit screen. Same path as the
// legacy single-setup GET, plus a linkId query to select a specific link.
export async function getOfflineAccessLinkSetupService(
  params: GetOneOfflineAccessLinkParams,
): Promise<OfflineAccessLink> {
  const { resourceType, resourceId, linkId, eventId } = params;
  const response = await instance.get<OfflineAccessLink>(
    `/OfflineAccess/Setup/${resourceType}/${resourceId}`,
    {
      ...RESOURCE_SCOPED_CONFIG,
      params: {
        linkId,
        ...(eventId ? { eventId } : {}),
      },
    },
  );
  return response.data;
}

// 2d. [Multi-link] Enable or disable a single link.
export async function setOfflineAccessLinkAccessService(
  args: SetLinkAccessArgs,
): Promise<void> {
  const { linkSettingId, enabled, teamId } = args;
  await instance.put(
    `/OfflineAccess/Links/${linkSettingId}/Access`,
    { enabled },
    {
      ...RESOURCE_SCOPED_CONFIG,
      params: teamId ? { teamId } : undefined,
    },
  );
}

// 2e. [Multi-link] Delete a single link.
export async function deleteOfflineAccessLinkService(
  args: DeleteOfflineAccessLinkArgs,
): Promise<void> {
  const { linkSettingId, teamId } = args;
  await instance.delete(`/OfflineAccess/Links/${linkSettingId}`, {
    ...RESOURCE_SCOPED_CONFIG,
    params: teamId ? { teamId } : undefined,
  });
}

// 3. Submit lead capture details for offline access.
export async function requestOfflineAccessService(
  payload: OfflineAccessRequestPayload,
): Promise<OfflineAccessRequestResponse> {
  const response = await instance.post<OfflineAccessRequestResponse>(
    "/OfflineAccess/Request",
    payload,
  );
  return response.data;
}

// 4. Verify the email OTP and obtain the presentaConnectionId.
export async function verifyOfflineAccessService(
  payload: OfflineAccessVerifyPayload,
): Promise<OfflineAccessVerifyResponse> {
  const response = await instance.post<OfflineAccessVerifyResponse>(
    "/OfflineAccess/Verify",
    payload,
  );
  return response.data;
}

// 5. Load questions for an offline-access resource.
export async function getOfflineAccessQuestionsService(
  params: OfflineAccessQuestionsParams,
): Promise<DiscussionInterface[]> {
  const { resourceType, resourceId, presentaConnectionId, slideId, linkId } =
    params;
  const response = await instance.get<DiscussionInterface[]>(
    `/OfflineAccess/Questions/${resourceType}/${resourceId}`,
    {
      params: {
        ...(presentaConnectionId ? { presentaConnectionId } : {}),
        ...(slideId ? { slideId } : {}),
        ...(linkId ? { linkId } : {}),
      },
    },
  );
  return response.data;
}

// 6. Ask a question for an offline-access resource.
export async function postOfflineAccessQuestionService(
  params: OfflineAccessQuestionsParams,
  payload: OfflineAccessQuestionPayload,
): Promise<DiscussionInterface> {
  const response = await instance.post<DiscussionInterface>(
    `/OfflineAccess/Questions/${params.resourceType}/${params.resourceId}`,
    payload,
  );
  return response.data;
}

// 7. Answer a question for an offline-access resource.
export async function answerOfflineAccessQuestionService(
  payload: OfflineAccessQuestionAnswerPayload,
): Promise<DiscussionInterface> {
  const response = await instance.post<DiscussionInterface>(
    `/OfflineAccess/Questions/${payload.resourceType}/${payload.resourceId}/${payload.questionId}/Answer`,
    { answer: payload.answer },
  );
  return response.data;
}

// 8. Upvote a question. `valid: true` records an up-vote; use
// removeOfflineAccessQuestionVoteService to take it back.
export async function voteOfflineAccessQuestionService(
  params: OfflineAccessQuestionVoteParams,
): Promise<void> {
  const { resourceType, resourceId, questionId, presentaConnectionId, linkId } =
    params;
  await instance.post(
    `/OfflineAccess/Questions/${resourceType}/${resourceId}/${questionId}/Vote`,
    {
      presentaConnectionId,
      valid: true,
      ...(linkId ? { settingId: linkId } : {}),
    },
  );
}

// 9. Remove a previously cast vote from a question.
export async function removeOfflineAccessQuestionVoteService(
  params: OfflineAccessQuestionVoteParams,
): Promise<void> {
  const { resourceType, resourceId, questionId, presentaConnectionId, linkId } =
    params;
  await instance.delete(
    `/OfflineAccess/Questions/${resourceType}/${resourceId}/${questionId}/Vote`,
    {
      params: {
        presentaConnectionId,
        ...(linkId ? { linkId } : {}),
      },
    },
  );
}

// 10. Send a direct message to the link owner.
export async function sendOfflineAccessDirectMessageService(
  payload: OfflineAccessDirectMessagePayload,
): Promise<void> {
  const { resourceType, resourceId, presentaConnectionId, message, settingId } =
    payload;
  await instance.post(
    `/OfflineAccess/DirectMessage/${resourceType}/${resourceId}`,
    {
      presentaConnectionId,
      message,
      ...(settingId ? { settingId } : {}),
    },
  );
}

// 11. Fetch the direct-message thread (same path as the POST, GET variant).
export async function getOfflineAccessDirectMessagesService(
  params: OfflineAccessDirectMessageParams,
): Promise<OfflineAccessDirectMessageItem[]> {
  const { resourceType, resourceId, presentaConnectionId, linkId } = params;
  const response = await instance.get<OfflineAccessDirectMessageItem[]>(
    `/OfflineAccess/DirectMessage/${resourceType}/${resourceId}`,
    {
      params: {
        ...(presentaConnectionId ? { presentaConnectionId } : {}),
        ...(linkId ? { linkId } : {}),
      },
    },
  );
  return response.data;
}
